import { useMutation, useQueryClient } from "@tanstack/react-query";
import { titleQueryKeys } from "@/lib/react-query/keyFactory";
import { proofingTitleCreate, proofingTitleUpdate } from "@/proofingService-client/sdk.gen";
import type {
  ProofingTitleCreateData,
  ProofingTitleCreateResponse,
  ProofingTitleUpdateData,
} from "@/proofingService-client/types.gen";

export interface UseCreateProofingTitleOptions {
  onSuccess?: (data: ProofingTitleCreateResponse) => void;
  onError?: (error: Error) => void;
}

export const useCreateProofingTitle = (options?: UseCreateProofingTitleOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProofingTitleCreateData["body"]) => {
      const response = await proofingTitleCreate({
        body: data,
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate and refetch titles list to show the new title
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      console.error("Failed to create proofing title:", error);
      options?.onError?.(error);
    },
  });
};

export interface UseUpdateProofingTitleOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for updating an existing proofing title with auto-save functionality.
 *
 * Supports optimistic updates and automatic query cache invalidation.
 * The mutation accepts partial updates - only fields that need to be changed
 * should be included in the update data.
 *
 * @param titleId - The ID of the title to update
 * @param options - Optional callbacks for success and error handling
 *
 * @example
 * ```tsx
 * const updateMutation = useUpdateProofingTitle(titleId, {
 *   onSuccess: () => console.log('Saved!'),
 *   onError: (error) => console.error('Save failed:', error)
 * });
 *
 * updateMutation.mutate({
 *   isbn: '978-3-16-148410-0',
 *   supplierId: 'supplier-123'
 * });
 * ```
 */
export const useUpdateProofingTitle = (
  titleId: string,
  options?: UseUpdateProofingTitleOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProofingTitleUpdateData["body"]) => {
      const response = await proofingTitleUpdate({
        path: { titleId },
        body: data,
      });
      return response.data;
    },
    onMutate: async (newData) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: titleQueryKeys.detail(titleId) });

      // Snapshot the previous value
      const previousTitle = queryClient.getQueryData(titleQueryKeys.detail(titleId));

      // Optimistically update to the new value
      queryClient.setQueryData(titleQueryKeys.detail(titleId), (old: any) => {
        if (!old) return old;
        return {
          ...old,
          ...newData,
          // Update the updatedAt timestamp optimistically
          updatedAt: new Date().toISOString(),
        };
      });

      // Return context with the previous value for rollback
      return { previousTitle };
    },
    onSuccess: (_data) => {
      // Invalidate queries to refetch fresh data from server
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.detail(titleId) });
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.();
    },
    onError: (error: Error, _newData, context) => {
      // Rollback to the previous value on error
      if (context?.previousTitle) {
        queryClient.setQueryData(titleQueryKeys.detail(titleId), context.previousTitle);
      }
      console.error("Failed to update proofing title:", error);
      options?.onError?.(error);
    },
  });
};
